import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import { getAccountById, getAccountPatients } from "../api";
import {
  getUnallocatedERAsForAccount,
  markERAClaimAllocated,
  markERAClaimDismissed,
} from "../afinanceapi";
import { EditAccount } from "../Components/PatientsAccounts/EditAccounts";
import { NotesHistory } from "../Components/PatientsAccounts/AccountNotes";
import AccountStatements from "../Components/Accounts/AccountStatements";
import { Breadcrumb } from '../Components/Breadcrumb/Breadcrumb';
import { getBookingsReturnPath } from "../utils/bookingsReturn";
import "../Components/Accounts/acc-rail.css";
import "../Components/Accounts/acc-list.css";

export function Accounts() {
  const { id } = useParams();
  const location = useLocation();

  const [account, setAccount] = useState(null);
  const [dependants, setDependants] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [activeSection, setActiveSection] = useState("account");
  const [unallocatedERAs, setUnallocatedERAs] = useState([]);
  const [allocatingKey, setAllocatingKey] = useState(null);
  const [allocatingAction, setAllocatingAction] = useState(null);
  const [expandedEraKeys, setExpandedEraKeys] = useState(() => new Set());
  // Tracks which rail item's submenu popover is open. Keyed by
  // `${prefix}-${item.key}` so mobile + desktop popovers don't collide.
  const [openSubKey, setOpenSubKey] = useState(null);
  // Imperative handle on AccountStatements — lets the rail trigger EDI /
  // Create Invoice / Receipt / Credit Note / Write Off / Download / Email /
  // Bulk Export without lifting all that state up here.
  const statementRef = useRef(null);
  // Status surfaced from AccountStatements for the page-header badges
  // (EDI-disabled state, written-off totals). Updated via the
  // onStateChange callback so this page re-renders when they change.
  const [statementState, setStatementState] = useState({
    ediActive: false,
    ediEndDate: null,
    hasWrittenOff: false,
  });
  const navigate = useNavigate();

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16).replace("T", " ");
  }, []);

  // Updated by the NotesHistory modal after Save/Clear Tag — keeps the
  // page-header badge and the coloured info-column strip in sync without
  // an extra round-trip to refetch the account.
  const handleTagChange = useCallback((tagText, tagColor) => {
    setAccount((prev) =>
      prev
        ? { ...prev, accountTagText: tagText, accountTagColor: tagColor }
        : prev,
    );
  }, []);

  // Thick coloured left border applied to the .acc-list container surface —
  // visually "part of" the account info card rather than a standalone strip.
  const tagContainerStyle = account?.accountTagText && account?.accountTagColor
    ? { borderLeft: `8px solid ${account.accountTagColor}` }
    : undefined;

  const hasMedicalAid = useMemo(() => {
    return !!(account?.accountMedicalAid && String(account.accountMedicalAid).trim());
  }, [account]);

  const hasIOD = useMemo(() => {
    return !!(account?.accountIODFund && String(account.accountIODFund).trim());
  }, [account]);

  const isInsurance = useMemo(() => {
    return (
      hasIOD &&
      String(account?.accountIODFundCategory || "").toUpperCase() === "INSURANCE"
    );
  }, [hasIOD, account]);

  const canAccessPatients = useMemo(() => {
    try {
      const token = sessionStorage.getItem('user');
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some(r => ['SuperAdmin', 'Receptionist'].includes(r));
    } catch { return false; }
  }, []);

  const canAccessAccounts = useMemo(() => {
    try {
      const token = sessionStorage.getItem('user');
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some(r => ['SuperAdmin', 'Finance'].includes(r));
    } catch { return false; }
  }, []);

  const breadcrumbItems = useMemo(() => {
    if (!account) return null;

    const fi = (account.accountFullNames || '').charAt(0);
    const accountLabel = `Account - ${fi ? fi + ' ' : ''}${account.accountLastName || ''}`.trim() || 'Account';
    const accountCrumb = { label: accountLabel, path: activeSection !== 'account' ? `/accounts/${id}` : undefined };

    let base;

    if (location.state?.fromAppointment) {
      // Appointment > [Date - Name] > [Account Name]
      base = [
        { label: 'Appointments', path: getBookingsReturnPath() },
        { label: location.state.appointmentLabel, path: location.state.appointmentPath },
        accountCrumb,
      ];
    } else if (location.state?.fromDashboard) {
      // Dashboard > [Patient Name] > [Account Name]
      base = [
        { label: 'Dashboard', path: '/home' },
        { label: location.state.patientName, path: `/patients/appointment/${location.state.patientId}/1`, canAccess: canAccessPatients },
        accountCrumb,
      ];
    } else if (location.state?.fromPatient) {
      // [Patient Name] > [Account Name]  (no broken /patients link)
      base = [
        { label: location.state.patientName, path: `/patients/appointment/${location.state.patientId}/1`, canAccess: canAccessPatients },
        accountCrumb,
      ];
    } else if (location.state?.breadcrumbTrail?.length) {
      // Generic upstream trail (e.g. from InvoiceList)
      base = [...location.state.breadcrumbTrail, accountCrumb];
    } else {
      // Accounts > [Account Name]
      base = [
        { label: 'Accounts', path: '/billing', canAccess: canAccessAccounts },
        accountCrumb,
      ];
    }

    return base;
  }, [account, activeSection, id, location.state, canAccessPatients, canAccessAccounts]);

  // ✅ Consistent icon spacing everywhere
  const IconLine = ({ icon, children, className = "" }) => (
    <div className={`d-flex align-items-start mb-1 ${className}`}>
      <i className={`${icon} text-primary me-2 mt-1`} />
      <div className="lh-sm">{children}</div>
    </div>
  );

  const getStatusClass = (status) =>
    status?.toLowerCase().includes("active") ? "text-success" : "text-danger";

  // ✅ Load account
  useEffect(() => {
    async function getAccountInfo() {
      try {
        const accountData = await getAccountById(id);
        setAccount(accountData);
        console.log("📥 Account fetched:", accountData);
      } catch (error) {
        console.error("Error fetching account data:", error);
      }
    }
    getAccountInfo();
  }, [id]);

  // ✅ Load dependants (patients on account)
  useEffect(() => {
    async function loadDependants() {
      try {
        if (!account?._id) return;
        const dependantsData = await getAccountPatients(id);
        setDependants(Array.isArray(dependantsData) ? dependantsData : []);
        console.log("📥 Dependants fetched:", dependantsData);
      } catch (error) {
        console.error("Error fetching dependants data:", error);
        setDependants([]);
      }
    }
    loadDependants();
  }, [id, account?._id]);

  // ✅ Load unallocated ERAs for the account (Finance/SuperAdmin only).
  // The backend joins on accountReference (era:Reference) with a fallback on
  // accountMedicalAidNumber for older ERAs.
  useEffect(() => {
    async function loadUnallocatedERAs() {
      if (!account?._id || !canAccessAccounts) {
        setUnallocatedERAs([]);
        return;
      }
      try {
        const data = await getUnallocatedERAsForAccount(account._id);
        setUnallocatedERAs(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching unallocated ERAs:", error);
        setUnallocatedERAs([]);
      }
    }
    loadUnallocatedERAs();
  }, [account?._id, canAccessAccounts]);

  // Build a stable key per row — schemeClaimId may repeat across ERAs but is
  // unique within an eraLog, so combine both.
  const rowKey = (era) => `${era.eraLogId}|${era.schemeClaimId}|${era.treatmentDate || ""}`;

  const handleResolveERA = async (era, action) => {
    const key = rowKey(era);
    setAllocatingKey(key);
    setAllocatingAction(action);
    try {
      const fn = action === "dismiss" ? markERAClaimDismissed : markERAClaimAllocated;
      // Show the spinner for at least 2s so the user sees confirmation before
      // the row vanishes. API call runs in parallel — total wait is max(API, 2s).
      await Promise.all([
        fn({
          eraLogId: era.eraLogId,
          schemeClaimId: era.schemeClaimId,
          treatmentDate: era.treatmentDate,
        }),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
      setUnallocatedERAs((prev) => prev.filter((e) => rowKey(e) !== key));
    } catch (error) {
      console.error(`Failed to ${action} ERA:`, error);
      alert(`Failed to ${action} ERA. Please try again.`);
    } finally {
      setAllocatingKey(null);
      setAllocatingAction(null);
    }
  };

  function renderAccountInfo() {
    if (!account?._id) return <p>Loading account information...</p>;

    return (
      <div className="acc-list" style={tagContainerStyle}>
        <Row className="align-items-center mb-3">
          <Col md={12}>
            {/* Heading row — account name on the left, "Unallocated ERAs"
                heading on the right, sharing one horizontal row. */}
            <Row className="mb-1">
              <Col md={4}>
                <h6 className="text-primary mb-0">
                  {account?.accountTitle} {account?.accountFullNames}{" "}
                  {account?.accountLastName} ({account?.accountFirstName})
                </h6>
              </Col>
              <Col md={8}>
                {canAccessAccounts && (
                  <h6 className="text-primary mb-0 d-none d-lg-block">
                    Unallocated ERAs
                  </h6>
                )}
              </Col>
            </Row>

            <Row>
              {/* Column 1 — contact details under the account name. */}
              <Col md={4}>
                {account?.accountNumber && (
                  <IconLine icon="fa-regular fa-hashtag">
                    {account.accountNumber}
                  </IconLine>
                )}
                {account?.accountIdNumber && (
                  <IconLine icon="fa-solid fa-id-card">
                    {account.accountIdNumber}
                  </IconLine>
                )}
                {account?.accountTelNumberCell && (
                  <IconLine icon="fas fa-phone-alt">
                    {account.accountTelNumberCell}
                  </IconLine>
                )}
                {account?.accountEmail && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail}</IconLine>
                )}
                {account?.accountEmail2 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail2}</IconLine>
                )}
                {account?.accountEmail3 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail3}</IconLine>
                )}
                {account?.accountEmail4 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail4}</IconLine>
                )}
                {account?.accountEmail5 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail5}</IconLine>
                )}
              </Col>

              {/* Columns 2+3 combined — Finance: unallocated ERAs table.
                  Non-finance: employment details. Address has been removed
                  in favour of a wider ERA table. */}
              <Col md={8}>
                {canAccessAccounts ? (
                  <>
                    {unallocatedERAs.length === 0 ? (
                      <div className="text-muted small">None outstanding.</div>
                    ) : (
                      <>
                      <div className="d-none d-lg-block" style={{ maxHeight: "165px", overflowY: "auto" }}>
                        <Table size="sm" bordered responsive className="mb-0 small">
                          <thead
                            className="table-primary"
                            style={{ position: "sticky", top: 0, zIndex: 1 }}
                          >
                            <tr>
                              <th
                                className="text-primary"
                                style={{ width: "9%" }}
                              >
                                Treatment
                              </th>
                              <th
                                className="text-primary"
                                style={{ width: "7%" }}
                              >
                                Tariff
                              </th>
                              <th
                                className="text-primary"
                                style={{ width: "21%" }}
                              >
                                Reason
                              </th>
                              <th
                                className="text-primary text-end"
                                style={{ width: "15%", whiteSpace: "nowrap" }}
                              >
                                Claimed
                              </th>
                              <th
                                className="text-primary text-end"
                                style={{ width: "15%", whiteSpace: "nowrap" }}
                              >
                                Practice
                              </th>
                              <th
                                className="text-primary text-end"
                                style={{ width: "15%", whiteSpace: "nowrap" }}
                              >
                                Patient
                              </th>
                              <th
                                className="text-primary text-center"
                                style={{ width: "9%" }}
                              >
                                Alloc
                              </th>
                              <th
                                className="text-primary text-center"
                                style={{ width: "9%" }}
                              >
                                Dism
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {unallocatedERAs.map((era) => {
                              const key = rowKey(era);
                              const lines = Array.isArray(era.lines) ? era.lines : [];
                              const totalClaimed = lines.reduce(
                                (s, l) => s + Number(l?.claimedAmount || 0),
                                0
                              );
                              const totalPractice = lines.reduce(
                                (s, l) => s + Number(l?.paidAmount || 0),
                                0
                              );
                              const totalPatient = lines.reduce(
                                (s, l) => s + Number(l?.patientPortion || 0),
                                0
                              );
                              const headerLabel =
                                era.patientName ||
                                [era.eraDate, era.medicalAidName].filter(Boolean).join(" • ") ||
                                "(claim)";
                              return (
                                <React.Fragment key={key}>
                                  <tr className="table-secondary">
                                    <td colSpan={3} className="fw-semibold">
                                      <span title={headerLabel}>{headerLabel}</span>
                                      {era.isNew && (
                                        <span
                                          className="badge rounded-pill bg-success ms-2"
                                          style={{ fontSize: "0.65rem" }}
                                        >
                                          New
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      className="text-end fw-semibold"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      R {totalClaimed.toFixed(2)}
                                    </td>
                                    <td
                                      className="text-end fw-semibold"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      R {totalPractice.toFixed(2)}
                                    </td>
                                    <td
                                      className="text-end fw-semibold"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      R {totalPatient.toFixed(2)}
                                    </td>
                                    <td className="text-center">
                                      {allocatingKey === key && allocatingAction === "allocate" ? (
                                        <Spinner
                                          animation="border"
                                          variant="primary"
                                          size="sm"
                                          role="status"
                                          aria-label="Allocating"
                                        />
                                      ) : (
                                        <Form.Check
                                          type="checkbox"
                                          aria-label="Mark claim allocated"
                                          title="Mark claim allocated"
                                          checked={false}
                                          disabled={allocatingKey === key}
                                          onChange={() => handleResolveERA(era, "allocate")}
                                        />
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {allocatingKey === key && allocatingAction === "dismiss" ? (
                                        <Spinner
                                          animation="border"
                                          variant="primary"
                                          size="sm"
                                          role="status"
                                          aria-label="Dismissing"
                                        />
                                      ) : (
                                        <Form.Check
                                          type="checkbox"
                                          aria-label="Dismiss claim"
                                          title="Dismiss claim"
                                          checked={false}
                                          disabled={allocatingKey === key}
                                          onChange={() => handleResolveERA(era, "dismiss")}
                                        />
                                      )}
                                    </td>
                                  </tr>
                                  {lines.length === 0 ? (
                                    <tr>
                                      <td colSpan={8} className="text-muted text-center">
                                        No line detail
                                      </td>
                                    </tr>
                                  ) : (
                                    lines.map((line, i) => {
                                      const reason =
                                        Array.isArray(line.reasonCodes) && line.reasonCodes.length
                                          ? line.reasonCodes
                                              .map((r) =>
                                                [r.code, r.description].filter(Boolean).join(" - ")
                                              )
                                              .join("; ")
                                          : "";
                                      return (
                                        <tr key={`${key}|line-${i}`}>
                                          <td>{line.dateOfService || ""}</td>
                                          <td>{line.tariffCode || ""}</td>
                                          <td title={reason}>{reason}</td>
                                          <td
                                            className="text-end"
                                            style={{ whiteSpace: "nowrap" }}
                                          >
                                            R {Number(line.claimedAmount || 0).toFixed(2)}
                                          </td>
                                          <td
                                            className="text-end"
                                            style={{ whiteSpace: "nowrap" }}
                                          >
                                            R {Number(line.paidAmount || 0).toFixed(2)}
                                          </td>
                                          <td
                                            className="text-end"
                                            style={{ whiteSpace: "nowrap" }}
                                          >
                                            R {Number(line.patientPortion || 0).toFixed(2)}
                                          </td>
                                          <td />
                                          <td />
                                        </tr>
                                      );
                                    })
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>

                      {/* Mobile: card per ERA claim — Phase plan step 1 */}
                      <div className="d-lg-none">
                        <h6 className="text-primary fw-bold mt-3 mb-2">
                          Unallocated Funds
                        </h6>
                        {unallocatedERAs.map((era) => {
                          const key = rowKey(era);
                          const lines = Array.isArray(era.lines) ? era.lines : [];
                          const totalClaimed = lines.reduce((s, l) => s + Number(l?.claimedAmount || 0), 0);
                          const totalPractice = lines.reduce((s, l) => s + Number(l?.paidAmount || 0), 0);
                          const totalPatient = lines.reduce((s, l) => s + Number(l?.patientPortion || 0), 0);
                          const headerLabel =
                            era.patientName ||
                            [era.eraDate, era.medicalAidName].filter(Boolean).join(" • ") ||
                            "(claim)";
                          const isExpanded = expandedEraKeys.has(key);
                          const toggle = () =>
                            setExpandedEraKeys((prev) => {
                              const next = new Set(prev);
                              if (next.has(key)) next.delete(key);
                              else next.add(key);
                              return next;
                            });
                          return (
                            <div className="acc-card" key={key}>
                              <div className="acc-card-top">
                                <span className="acc-card-id" title={headerLabel}>
                                  {headerLabel}
                                  {era.isNew && (
                                    <span
                                      className="badge rounded-pill bg-success ms-2"
                                      style={{ fontSize: "0.65rem" }}
                                    >
                                      New
                                    </span>
                                  )}
                                </span>
                                <span className="acc-card-amount">R {totalClaimed.toFixed(2)}</span>
                              </div>
                              <div className="acc-card-body">
                                <div className="acc-card-field">
                                  <span className="acc-card-label">Practice</span>
                                  <span className="acc-card-value acc-list-num">R {totalPractice.toFixed(2)}</span>
                                </div>
                                <div className="acc-card-field">
                                  <span className="acc-card-label">Patient</span>
                                  <span className="acc-card-value acc-list-num">R {totalPatient.toFixed(2)}</span>
                                </div>
                              </div>
                              {lines.length > 0 && (
                                <Button
                                  variant="link"
                                  className="p-0 mt-2 acc-card-expand-hint"
                                  onClick={toggle}
                                  aria-expanded={isExpanded}
                                >
                                  <i className={`fa-solid me-1 ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"}`} />
                                  {isExpanded ? "Hide lines" : `Show ${lines.length} line${lines.length === 1 ? "" : "s"}`}
                                </Button>
                              )}
                              {isExpanded && lines.length > 0 && (
                                <div className="acc-card-lines">
                                  {lines.map((line, i) => {
                                    const reason =
                                      Array.isArray(line.reasonCodes) && line.reasonCodes.length
                                        ? line.reasonCodes
                                            .map((r) => [r.code, r.description].filter(Boolean).join(" - "))
                                            .join("; ")
                                        : "";
                                    return (
                                      <div className="acc-card-line" key={`${key}|line-${i}`}>
                                        <span className="acc-card-line-code">{line.tariffCode || "—"}</span>
                                        <span className="acc-card-line-rate">R {Number(line.claimedAmount || 0).toFixed(2)}</span>
                                        {reason && <span className="acc-card-line-desc">{reason}</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              <div className="d-flex gap-2 mt-2">
                                <Button
                                  variant="outline-primary"
                                  className="flex-fill"
                                  disabled={allocatingKey === key}
                                  onClick={() => handleResolveERA(era, "allocate")}
                                >
                                  {allocatingKey === key && allocatingAction === "allocate" ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <>
                                      <i className="fa-solid fa-check me-1" />
                                      Allocate
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  className="flex-fill"
                                  disabled={allocatingKey === key}
                                  onClick={() => handleResolveERA(era, "dismiss")}
                                >
                                  {allocatingKey === key && allocatingAction === "dismiss" ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <>
                                      <i className="fa-solid fa-xmark me-1" />
                                      Dismiss
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {account?.accountOccupation && (
                      <IconLine icon="fas fa-briefcase">{account.accountOccupation}</IconLine>
                    )}
                    {account?.accountEmployer && (
                      <IconLine icon="fas fa-building">{account.accountEmployer}</IconLine>
                    )}
                    {account?.accountTelNumberWork && (
                      <IconLine icon="fas fa-phone-alt">{account.accountTelNumberWork}</IconLine>
                    )}
                  </>
                )}
              </Col>
            </Row>
          </Col>
        </Row>

      </div>
    );
  }

  function renderCoverInformation() {
    if (!account?._id) return <p>Loading account information...</p>;

    // ✅ Title rules you asked for
    const sectionTitle = hasIOD
      ? isInsurance
        ? "Insurance Information"
        : "Injury on Duty Information"
      : hasMedicalAid
      ? "Medical Aid Information"
      : "Private Account Information";

    // ✅ Sort dependants numerically by dependant code (only meaningful for medical aid)
    const sortedDependants = [...dependants].sort((a, b) => {
      const aCode = parseInt(a.patientMedicalAidDependantCode || "0", 10);
      const bCode = parseInt(b.patientMedicalAidDependantCode || "0", 10);
      return aCode - bCode;
    });

    return (
      <div className="acc-list">
        <Row className="mb-1">
          <Col md={10}>
            <Row>
              {/* LEFT: Cover info */}
              <Col md={4}>
                <h6 className="text-primary">{sectionTitle}</h6>

                {/* ✅ IOD */}
                {hasIOD && (
                  <>
                    <IconLine icon="fa-solid fa-briefcase-medical">
                      <strong>{isInsurance ? "Insurance Fund:" : "IOD Fund:"}</strong> {account.accountIODFund}
                    </IconLine>

                    {(account.accountIODPlan || account.accountIODOption) && (
                      <div className="ps-4 text-muted small mb-1">
                        {account.accountIODPlan || "-"}
                        {account.accountIODPlan && account.accountIODOption ? " | " : ""}
                        {account.accountIODOption || ""}
                      </div>
                    )}

                    {account.accountIODClaimRefNumber && (
                      <IconLine icon="fa-solid fa-hashtag">
                        <strong>Claim Ref #:</strong> {account.accountIODClaimRefNumber}
                      </IconLine>
                    )}

                    {account.accountIODEmployer && (
                      <IconLine icon="fa-solid fa-building">
                        <strong>Employer:</strong> {account.accountIODEmployer}
                      </IconLine>
                    )}

                    {account.accountIODEmployerWCARegNum && (
                      <IconLine icon="fa-solid fa-id-card-clip">
                        <strong>Employer WCA Reg #:</strong>{" "}
                        {account.accountIODEmployerWCARegNum}
                      </IconLine>
                    )}

                    {account.accountIODDateOfInjury && (
                      <IconLine icon="fa-solid fa-calendar-days">
                        <strong>Date of Injury:</strong> {account.accountIODDateOfInjury}
                      </IconLine>
                    )}
                  </>
                )}

                {/* ✅ Medical Aid */}
                {!hasIOD && hasMedicalAid && (
                  <>
                    <IconLine icon="fa fa-notes-medical">
                      <strong>Medical Aid:</strong> {account.accountMedicalAid}
                    </IconLine>

                    {(account.accountMedicalAidPlan || account.accountMedicalAidOption) && (
                      <div className="ps-4 text-muted small mb-1">
                        {account.accountMedicalAidPlan || "-"}
                        {account.accountMedicalAidPlan && account.accountMedicalAidOption
                          ? " | "
                          : ""}
                        {account.accountMedicalAidOption || ""}
                      </div>
                    )}

                    {account.accountMedicalAidNumber && (
                      <IconLine icon="fa-solid fa-hashtag">
                        <strong>Number:</strong> {account.accountMedicalAidNumber}
                      </IconLine>
                    )}
                  </>
                )}

                {/* ✅ Private */}
                {!hasIOD && !hasMedicalAid && (
                  <div className="text-muted small">
                    No Medical Aid or IOD cover captured for this account.
                  </div>
                )}
              </Col>

              {/* RIGHT: Dependants (only meaningful when Medical Aid exists) */}
              <Col md={8}>
                <h6 className="text-primary">Dependants</h6>

                {sortedDependants.length > 0 ? (
                  <>
                    {/* Desktop: 3-column grid (existing) */}
                    <Row as="ul" className="list-unstyled d-none d-lg-block">
                      {sortedDependants.map((p) => {
                        const openPatient = () => {
                          const fi = (account.accountFullNames || '').charAt(0);
                          const acctLabel = `Account - ${fi ? fi + ' ' : ''}${account.accountLastName || ''}`.trim() || 'Account';
                          const ancestors = breadcrumbItems ? breadcrumbItems.slice(0, -1) : [];
                          const trail = [...ancestors, { label: acctLabel, path: `/accounts/${id}` }];
                          navigate(`/patients/appointment/${p._id}/1`, { state: { breadcrumbTrail: trail } });
                        };
                        return (
                          <Row
                            as="li"
                            key={p?._id}
                            className="d-flex align-items-start mb-1"
                          >
                            <Col md={4}>
                              <div
                                className="text-primary"
                                style={{ cursor: "pointer" }}
                                onClick={openPatient}
                              >
                                {p?.patientMedicalAidDependantCode
                                  ? `${p.patientMedicalAidDependantCode} - `
                                  : ""}
                                {p?.patientFirstName} {p?.patientLastName}
                              </div>
                            </Col>

                            {/* ✅ Only show these when Medical Aid exists on the account */}
                            {hasMedicalAid && (
                              <>
                                <Col md={4}>
                                  <div className={getStatusClass(p?.patientMedicalAidStatus)}>
                                    {p?.patientMedicalAidStatus || ""}
                                    {p?.patientMedicalAidJoinDate
                                      ? ` ${p.patientMedicalAidJoinDate}`
                                      : ""}
                                  </div>
                                </Col>

                                <Col md={4} className="text-start">
                                  <div className="text-muted">
                                    checked: {formatDate(p?.patientMedicalAidLastCheck)}
                                  </div>
                                </Col>
                              </>
                            )}
                          </Row>
                        );
                      })}
                    </Row>

                    {/* Mobile: card per dependant — Phase plan step 1 */}
                    <div className="d-lg-none">
                      {sortedDependants.map((p) => {
                        const openPatient = () => {
                          const fi = (account.accountFullNames || '').charAt(0);
                          const acctLabel = `Account - ${fi ? fi + ' ' : ''}${account.accountLastName || ''}`.trim() || 'Account';
                          const ancestors = breadcrumbItems ? breadcrumbItems.slice(0, -1) : [];
                          const trail = [...ancestors, { label: acctLabel, path: `/accounts/${id}` }];
                          navigate(`/patients/appointment/${p._id}/1`, { state: { breadcrumbTrail: trail } });
                        };
                        const depName = `${p?.patientFirstName || ""} ${p?.patientLastName || ""}`.trim();
                        return (
                          <div
                            className="acc-card"
                            key={p?._id}
                            onClick={openPatient}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openPatient();
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="acc-card-top">
                              <span className="acc-card-id">
                                {p?.patientMedicalAidDependantCode
                                  ? `${p.patientMedicalAidDependantCode} - `
                                  : ""}
                                {depName}
                              </span>
                            </div>
                            {hasMedicalAid && (
                              <div className="acc-card-body">
                                <div className="acc-card-field">
                                  <span className="acc-card-label">Status</span>
                                  <span className={`acc-card-value ${getStatusClass(p?.patientMedicalAidStatus)}`}>
                                    {p?.patientMedicalAidStatus || "-"}
                                    {p?.patientMedicalAidJoinDate ? ` ${p.patientMedicalAidJoinDate}` : ""}
                                  </span>
                                </div>
                                <div className="acc-card-field">
                                  <span className="acc-card-label">Checked</span>
                                  <span className="acc-card-value text-muted">
                                    {formatDate(p?.patientMedicalAidLastCheck)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-muted mb-0">No associated patients found.</p>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    );
  }

  // Rail items — two views: account info + the cover view (whose label/icon
  // shifts between IOD / Insurance / Medical Aid / Private depending on what
  // the account actually has, mirroring the prior heading logic).
  const coverLabel = hasIOD
    ? (isInsurance ? "Insurance Information" : "Injury on Duty Information")
    : hasMedicalAid
      ? "Medical Aid Information"
      : "Private Account Information";
  const coverIcon = hasIOD
    ? (isInsurance ? "fa-light fa-shield-heart" : "fa-light fa-briefcase-medical")
    : hasMedicalAid
      ? "fa-light fa-notes-medical"
      : "fa-light fa-user";
  // Rail items: section selectors + action triggers, interleaved so each
  // section icon is followed by its related action:
  //   Account Info → Edit, Cover Info → Notes, EDI, Actions (submenu)
  // `type: "action"` invokes onClick directly. `type: "submenu"` opens a
  // popover (practice.jsx pattern) with `children` rendered as buttons.
  const sidebarItems = [
    { key: "account",    label: "Account Information", short: "Account",  icon: "fa-light fa-address-card",  type: "section" },
    { key: "edit",       label: "Edit Account",        short: "Edit",     icon: "fa-light fa-pen-to-square", type: "action",  onClick: () => setShowEdit(true) },
    { key: "medicalAid", label: coverLabel,            short: "Cover",    icon: coverIcon,                   type: "section" },
    { key: "notes",      label: "Notes & History",     short: "Notes",    icon: "fa-light fa-note-sticky",   type: "action",  onClick: () => setShowNotes(true) },
    ...(hasMedicalAid
      ? [
          {
            key: "edi",
            label: "EDI",
            short: "EDI",
            icon: "fa-light fa-tower-broadcast",
            type: "action",
            onClick: () => statementRef.current?.openEdi(),
          },
        ]
      : []),
    {
      key: "actions",
      label: "Statement Actions",
      short: "Actions",
      icon: "fa-light fa-gears",
      type: "submenu",
      children: [
        { key: "createInvoice", label: "Create Invoice", icon: "fa fa-file-invoice-dollar", gated: true, onClick: () => statementRef.current?.createInvoice() },
        { key: "createReceipt", label: "Receipt",        icon: "fa fa-receipt",              gated: true, onClick: () => statementRef.current?.createReceipt() },
        { key: "creditNote",    label: "Credit Note",    icon: "fa fa-undo",                 gated: true, onClick: () => statementRef.current?.createCreditNote() },
        { key: "writeOff",      label: "Write Off",      icon: "fa fa-ban",                  gated: true, onClick: () => statementRef.current?.createWriteOff() },
        { key: "downloadPdf",   label: "Download PDF",   icon: "fa fa-download",                          onClick: () => statementRef.current?.downloadPdf() },
        { key: "emailStmt",     label: "Email Statement", icon: "fa-regular fa-envelope",                 onClick: () => statementRef.current?.emailStatement() },
        { key: "bulkExport",    label: "Bulk Export",    icon: "fa-solid fa-file-export",                 onClick: () => statementRef.current?.bulkExport() },
      ],
    },
  ];

  // Render a single rail item — handles section / action / submenu variants
  // identically for the desktop rail and the mobile tab strip. `prefix`
  // namespaces the open-submenu state so the two rails don't collide.
  // Submenu items render as a Bootstrap Dropdown (compact dropdown-item
  // styling via `actions-dropdown`) to match PostedInvoice's Actions menu.
  const renderRailItem = (item, { prefix, popPlacement }) => {
    const isAction = item.type === "action";
    const isSubmenu = item.type === "submenu";
    const isActive = !isAction && !isSubmenu && activeSection === item.key;
    const submenuKey = `${prefix}-${item.key}`;
    if (isSubmenu) {
      const dropDirection = popPlacement === "right" ? "end" : "down";
      const menuAlign = popPlacement === "right" ? "start" : "end";
      return (
        <Dropdown
          key={submenuKey}
          drop={dropDirection}
          align={menuAlign}
          show={openSubKey === submenuKey}
          onToggle={(show) => setOpenSubKey(show ? submenuKey : null)}
        >
          <Dropdown.Toggle
            as={Button}
            aria-label={item.label}
            title={item.label}
            className="acc-rail-item acc-rail-item-labeled"
          >
            <i className={item.icon} />
            <span className="acc-rail-label">{item.short}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="shadow-sm actions-dropdown"
            renderOnMount
            popperConfig={{
              strategy: "fixed",
              modifiers: [
                {
                  name: "computeStyles",
                  options: { adaptive: false, gpuAcceleration: false },
                },
              ],
            }}
          >
            {item.children
              .filter((sub) => !sub.gated || canAccessAccounts)
              .map((sub) => (
                <Dropdown.Item
                  key={sub.key}
                  onClick={() => {
                    sub.onClick?.();
                    setOpenSubKey(null);
                  }}
                >
                  {sub.icon && <i className={`${sub.icon} me-2`} />}
                  {sub.label}
                </Dropdown.Item>
              ))}
          </Dropdown.Menu>
        </Dropdown>
      );
    }
    const buttonNode = (
      <Button
        aria-label={item.label}
        title={item.label}
        className={
          isActive
            ? "acc-rail-item acc-rail-item-labeled active"
            : "acc-rail-item acc-rail-item-labeled"
        }
        onClick={isAction ? item.onClick : () => setActiveSection(item.key)}
      >
        <i className={item.icon} />
        <span className="acc-rail-label">{item.short}</span>
      </Button>
    );
    return React.cloneElement(buttonNode, { key: submenuKey });
  };
  const activeItem = sidebarItems.find(
    (i) => i.type !== "action" && i.key === activeSection
  );

  return (
    <div style={{ marginTop: "50px" }}>
      {/* Controlled-mode modals — triggered by the mobile-only page-header
          kebab. The shared EditAccount / NotesHistory wrappers in
          PatientsAccounts/ accept optional show + onHide; when supplied they
          skip their internal trigger button. */}
      {account?._id && (
        <>
          <EditAccount
            account={account}
            show={showEdit}
            onHide={() => setShowEdit(false)}
          />
          <NotesHistory
            account={account}
            show={showNotes}
            onHide={() => setShowNotes(false)}
            onTagChange={handleTagChange}
          />
        </>
      )}

      <Container fluid className="px-0">
        <Row className="g-0">
          {/* MOBILE TAB STRIP (<768px) — horizontal-scroll icon row */}
          <Col xs={12} className="d-flex d-lg-none g-0">
            <Container className="bg-white g-0 shadow-sm rounded">
              <div className="acc-rail-mobile">
                {sidebarItems.map((item) =>
                  renderRailItem(item, { prefix: "mobile", popPlacement: "bottom" })
                )}
              </div>
            </Container>
          </Col>

          {/* LEFT PANEL — DESKTOP ICON RAIL (>=768px). The white container
              stretches the full page length (h-100 fills the Col which
              stretches to row height). The buttons inside are sticky so
              they stay visible at top:55px while the long content scrolls. */}
          <Col xs="auto" className="d-none d-lg-block g-0" style={{ width: 64 }}>
            <Container
              className="bg-white g-0 p-1 shadow-sm rounded h-100"
               style={{ width: 64, minHeight: "100vh" }}  
            >
              <div
                className="acc-rail"
                style={{ position: "sticky", top: 55 }}
              >
                {sidebarItems.map((item) =>
                  renderRailItem(item, { prefix: "desktop", popPlacement: "right" })
                )}
              </div>
            </Container>
          </Col>

          {/* RIGHT CONTENT */}
          <Col xs={12} md className="g-0">
            <Container fluid className="pt-3 bg-transparent pb-3 mb-3 rounded">
              {/* Breadcrumb (naked) at the top of the content column. Edit /
                  Notes triggers live in the icon rail (mobile + desktop). */}
              {breadcrumbItems && (
                <div className="ms-1 mb-2">
                  <Breadcrumb items={breadcrumbItems} />
                </div>
              )}
              {/* Section heading on the left, status badges (Written Off,
                  EDI Disabled until X) right-aligned. Badges come from
                  AccountStatements via onStateChange. */}
              <div className="d-flex align-items-center flex-wrap gap-2 mb-3 ms-1 me-1 justify-content-between">
                <h4 className="text-primary fw-normal mb-0">
                  {activeItem?.label}
                </h4>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  {account?.accountTagText && account?.accountTagColor && (
                    <span
                      className="badge py-2 px-3"
                      style={{
                        backgroundColor: account.accountTagColor,
                        color: "#fff",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                      title={account.accountTagText}
                    >
                      {account.accountTagText}
                    </span>
                  )}
                  {statementState.hasWrittenOff && (
                    <span
                      className="badge bg-danger py-2 px-3"
                      title="Written Off"
                    >
                      <i className="fa fa-ban" />
                      <span className="d-none d-lg-inline ms-1">Written Off</span>
                    </span>
                  )}
                  {statementState.ediActive && (
                    <span
                      className="badge bg-warning text-dark py-2 px-3"
                      style={{ whiteSpace: "nowrap" }}
                      title={`EDI Disabled until ${statementState.ediEndDate}`}
                    >
                      <i className="fa-solid fa-tower-broadcast" />
                      <span className="d-none d-lg-inline ms-1">
                        EDI Disabled until {statementState.ediEndDate}
                      </span>
                    </span>
                  )}
                </div>
              </div>
              {activeSection === "account" && renderAccountInfo()}
              {activeSection === "medicalAid" && renderCoverInformation()}

              <Row className="ms-1 me-1 mt-3">
                <AccountStatements
                  ref={statementRef}
                  account={account}
                  breadcrumbContext={location.state}
                  onStateChange={setStatementState}
                />
              </Row>
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
